
import { Options, Vue } from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { MultisidebarItem } from '@/store/multisidebar/types';
import { ItemViewModel } from '@/definitions/view-models';
import { ItemAclResult, ModelAclResult } from '@/store/acl/types';
import MultisidebarHeader from '@/components/multisidebar/MultisidebarHeader.vue';
import NTabs from '@/uikit/tabs/NTabs.vue';
import NForm from '@/uikit/forms/NForm.vue';
import { Group } from '@/api';
import watchListsLayout from '@/pages/groups/group-watch-lists';
import cameraGroupsLayout from '@/pages/groups/group-camera-groups';
import permissionsLayout from '@/pages/groups/group-permissions';
import { adGroupFromItem, nameFromItem } from '@/pages/groups/group-information';
import { Action } from '@/uikit';
import { actionsModule } from '@/store/data/ActionsModule';
import { configModule } from '@/store/config';
import { ItemsActionName } from '@/definitions/app/item.actions.name';
import { actionHandler } from '@/store/data/ActionHandler';
import MultisidebarSaveActions from '@/components/multisidebar/MultisidebarSaveActions.vue';
import { dataModule } from '@/store/data';
import { globalEventModule, GlobalEventName } from '@/store/global-event';
import { MultisidebarItemTypes } from '@/store/multisidebar/types';
import { GlobalEvent } from '@/store/global-event/types';
import { getItemCommonFields } from '@/components/common/get-item-common-fields';

const Tabs = {
  Information: 'information',
  WatchLists: 'watch_lists',
  CameraGroups: 'camera_groups',
  Permissions: 'permissions'
};

@Options({
  name: 'GroupsSidebar',
  components: { MultisidebarSaveActions, NForm, NTabs, MultisidebarHeader }
})
export default class GroupsSidebar extends Vue {
  @Prop({ type: Object, required: true })
  readonly sidebarItem!: MultisidebarItem<ItemViewModel<Group>>;

  @Prop({ type: Object, required: true })
  readonly modelAcl!: ModelAclResult;

  tab = 'information';

  get computedModelAcl() {
    return dataModule.isCurrentUserAdmin ? { view: true, update: true, delete: true, add: true } : this.modelAcl;
  }

  get model() {
    return this.sidebarItem.model;
  }

  get sidebarForm() {
    switch (this.tab) {
      case Tabs.WatchLists:
        return watchListsLayout;
      case Tabs.CameraGroups:
        return cameraGroupsLayout;
      case Tabs.Permissions:
        return permissionsLayout;
      default:
        return this.infoLayout;
    }
  }

  get infoLayout() {
    const base = this.isADEnabled ? [nameFromItem, adGroupFromItem] : [nameFromItem];
    !this.model.isNew && base.push(...getItemCommonFields());
    return base;
  }

  get isADEnabled() {
    return configModule.config.services.ffsecurity.active_directory;
  }

  get currentTabItems() {
    return this.editMode ? this.tabItems : this.tabItems.slice(0, 1);
  }

  get editMode() {
    return !this.model.isNew;
  }

  get tabItems() {
    return [
      {
        name: Tabs.Information,
        label: this.$t(`common.${Tabs.Information}`, 'f')
      },
      {
        name: Tabs.WatchLists,
        label: this.$t(`common.${Tabs.WatchLists}`, 'f')
      },
      {
        name: Tabs.CameraGroups,
        label: this.$t(`common.${Tabs.CameraGroups}`, 'f')
      },
      {
        name: Tabs.Permissions,
        label: this.$t(`common.${Tabs.Permissions}`, 'f')
      }
    ];
  }

  get headerActions(): Action[] {
    return actionsModule
      .getItemActions(this.modelAcl, null, {
        hasDelete: true
      })
      .map(actionsModule.computeActionByName);
  }

  get lastPageEvent() {
    return globalEventModule.current?.type === MultisidebarItemTypes.Groups ? globalEventModule.current : null;
  }

  @Watch('lastPageEvent')
  refreshUserDescriptors(event: GlobalEvent) {
    if (event?.name === GlobalEventName.Update) {
      dataModule.loadCurrentUserModule();
    }
  }

  handleAction(actionName: ItemsActionName) {
    actionHandler.run(actionName, this.sidebarItem);
  }

  validateForm() {
    const result = this.$refs.form ? this.$refs.form.validate() : true;
    if (!result) {
      this.$refs.form.displayErrors();
    }
    return result;
  }

  created() {
    actionHandler.getIsItemFormValid = this.validateForm.bind(this);
  }
}
